/**
 * Stable shared contract for worker CHSP operational capture. It validates
 * transport shape only: values are evidence, never clinical decisions or
 * finance authority. Unknown form versions remain capturable and are held for
 * provider review by the database capture boundary.
 */
export const CHSP_VISIT_CAPTURE_FORM_VERSION = 'chsp-visit-capture/1' as const;

/** Canonical CHSP service catalogue shared by coordinator and worker capture. */
export const CHSP_SERVICE_TYPES = [
  { value: 'ALLIED_HEALTH_THERAPY', label: 'Allied health and therapy', group: 'Home Support' },
  { value: 'COMMUNITY_COTTAGE_RESPITE', label: 'Community cottage respite', group: 'Home Support' },
  { value: 'DOMESTIC_ASSISTANCE', label: 'Domestic assistance', group: 'Home Support' },
  { value: 'HOARDING_SQUALOR', label: 'Hoarding and squalor assistance', group: 'Home Support' },
  {
    value: 'HOME_MAINTENANCE_REPAIRS',
    label: 'Home maintenance and repairs',
    group: 'Home Support',
  },
  {
    value: 'HOME_COMMUNITY_GENERAL_RESPITE',
    label: 'Home or community general respite',
    group: 'Home Support',
  },
  { value: 'MEALS', label: 'Meals', group: 'Home Support' },
  { value: 'NURSING_CARE', label: 'Nursing care', group: 'Home Support' },
  { value: 'PERSONAL_CARE', label: 'Personal care', group: 'Home Support' },
  {
    value: 'SOCIAL_SUPPORT_ENGAGEMENT',
    label: 'Social support and community engagement',
    group: 'Home Support',
  },
  {
    value: 'THERAPEUTIC_INDEPENDENT_LIVING',
    label: 'Therapeutic services for independent living',
    group: 'Home Support',
  },
  { value: 'TRANSPORT', label: 'Transport', group: 'Home Support' },
  {
    value: 'SPECIALISED_VISION_ADVISORY',
    label: 'Specialised support services (vision advisory)',
    group: 'Home Support',
  },
  { value: 'EQUIPMENT_PRODUCTS', label: 'Equipment and products', group: 'Assistive Technology' },
  { value: 'HOME_ADJUSTMENTS', label: 'Home adjustments', group: 'Home Modifications' },
] as const;

export type ChspServiceType = (typeof CHSP_SERVICE_TYPES)[number]['value'];

export const CHSP_SERVICE_TYPE_ALIASES: Readonly<Record<string, ChspServiceType>> = {
  HOARDING_SQUALOR_ASSISTANCE: 'HOARDING_SQUALOR',
  SOCIAL_SUPPORT_COMMUNITY_ENGAGEMENT: 'SOCIAL_SUPPORT_ENGAGEMENT',
  THERAPEUTIC_SERVICES_INDEPENDENT_LIVING: 'THERAPEUTIC_INDEPENDENT_LIVING',
  SPECIALISED_SUPPORT_VISION_ADVISORY: 'SPECIALISED_VISION_ADVISORY',
};

export const CHSP_INDIVIDUAL_VISIT_TYPES: ReadonlySet<ChspServiceType> = new Set([
  'ALLIED_HEALTH_THERAPY',
  'DOMESTIC_ASSISTANCE',
  'HOARDING_SQUALOR',
  'HOME_MAINTENANCE_REPAIRS',
  'HOME_COMMUNITY_GENERAL_RESPITE',
  'NURSING_CARE',
  'PERSONAL_CARE',
  'SOCIAL_SUPPORT_ENGAGEMENT',
  'THERAPEUTIC_INDEPENDENT_LIVING',
  'SPECIALISED_VISION_ADVISORY',
]);

/** CHSP services whose current capture contract permits a group session. */
export const CHSP_GROUP_SESSION_TYPES: ReadonlySet<ChspServiceType> = new Set([
  'ALLIED_HEALTH_THERAPY',
  'HOME_COMMUNITY_GENERAL_RESPITE',
  'SOCIAL_SUPPORT_ENGAGEMENT',
  'THERAPEUTIC_INDEPENDENT_LIVING',
]);

export type ChspOperationalFieldValue = string | number | boolean | string[] | null;
export type ChspOperationalFields = Record<string, ChspOperationalFieldValue>;

const FIELD_KEY = /^[a-z][a-z0-9_]{0,63}$/;
const MAX_FIELDS = 40;
const MAX_FIELD_BYTES = 2048;
const MAX_TOTAL_BYTES = 8192;

export type ChspOperationalCaptureValidation =
  | { valid: true; fields: ChspOperationalFields; formVersionKnown: boolean }
  | { valid: false; error: 'invalid_operational_fields' };

/**
 * Validate bounded, structured evidence while allowing an older offline client
 * to preserve its payload. The server records unfamiliar form versions as a
 * deficiency; it does not reject the care capture.
 */
export function validateChspOperationalCapture(input: {
  formVersion: unknown;
  fields: unknown;
}): ChspOperationalCaptureValidation {
  const fields = input.fields;
  if (
    fields === null ||
    typeof fields !== 'object' ||
    Array.isArray(fields) ||
    Object.getPrototypeOf(fields) !== Object.prototype
  ) {
    return { valid: false, error: 'invalid_operational_fields' };
  }
  const entries = Object.entries(fields as Record<string, unknown>);
  if (entries.length > MAX_FIELDS) return { valid: false, error: 'invalid_operational_fields' };
  const normalised: ChspOperationalFields = {};
  let totalBytes = 0;
  for (const [key, value] of entries) {
    if (!FIELD_KEY.test(key)) return { valid: false, error: 'invalid_operational_fields' };
    if (
      value !== null &&
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean' &&
      !(
        Array.isArray(value) &&
        value.every((item) => typeof item === 'string' && item.length <= 160)
      )
    ) {
      return { valid: false, error: 'invalid_operational_fields' };
    }
    if (typeof value === 'number' && !Number.isFinite(value))
      return { valid: false, error: 'invalid_operational_fields' };
    if (typeof value === 'string' && value.length > MAX_FIELD_BYTES)
      return { valid: false, error: 'invalid_operational_fields' };
    const bytes = JSON.stringify([key, value]).length;
    totalBytes += bytes;
    if (totalBytes > MAX_TOTAL_BYTES) return { valid: false, error: 'invalid_operational_fields' };
    normalised[key] = value as ChspOperationalFieldValue;
  }
  return {
    valid: true,
    fields: normalised,
    formVersionKnown: input.formVersion === CHSP_VISIT_CAPTURE_FORM_VERSION,
  };
}
